// ============================================
// pages/AccountDetails.jsx
// ============================================
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiMail, 
  FiPhone, 
  FiLock,
  FiChevronDown,
  FiCheck,
  FiX,
  FiAlertCircle
} from 'react-icons/fi';
import DashboardLayout from '../components/DashboardLayout';
import PhoneCodePicker from '../components/PhoneCodePicker';
import { useAuth } from '../services/auth';
import { getCurrentUserProfile, updateUserProfile } from '../services/userService';
import supabase from '../services/supabase';
import '../styles/AccountDetails.scss';

import { sendPhoneCode, verifyPhoneCode } from '../services/authCodes';
const CLOUD_FUNCTIONS_BASE_URL = 'https://asia-south1-invits-app.cloudfunctions.net';

const AccountDetails = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Email state
  const [email, setEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [sendingEmailOtp, setSendingEmailOtp] = useState(false);
  
  // Email OTP state
  const [showEmailOtp, setShowEmailOtp] = useState(false);
  const [emailOtp, setEmailOtp] = useState(['', '', '', '', '', '']);
  const emailOtpInputs = useRef([]);
  const [verifyingEmailOtp, setVerifyingEmailOtp] = useState(false);
  
  // Phone state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [showPhoneInput, setShowPhoneInput] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState({
    code: '+91',
    flag: '🇮🇳',
    name: 'India',
    iso2: 'IN',
  });
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  
  // OTP state for phone
  const [showPhoneOtp, setShowPhoneOtp] = useState(false);
  const [phoneOtp, setPhoneOtp] = useState(['', '', '', '', '', '']);
  const phoneOtpInputs = useRef([]);
  
  // Password reset state
  const [resetPasswordState, setResetPasswordState] = useState('idle');
  const [resetPasswordTimer, setResetPasswordTimer] = useState(0);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [updatingPhone, setUpdatingPhone] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  useEffect(() => {
    let interval;
    if (resetPasswordTimer > 0) {
      interval = setInterval(() => {
        setResetPasswordTimer((prev) => {
          if (prev <= 1) {
            setResetPasswordState('idle');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resetPasswordTimer]);

  const loadProfile = async () => {
    try {
      const profile = await getCurrentUserProfile();
      if (profile) {
        const isTempEmail = profile.email?.includes('@temp.invits.co');
        setEmail(isTempEmail ? '' : profile.email || '');
        setEmailVerified(profile.email_verified || false);
        setPhoneNumber(profile.phone_number || '');
        
        if (profile.country_code) {
          setSelectedCountry({
            code: profile.country_code,
            flag: getCountryFlag(profile.country_code),
            name: getCountryName(profile.country_code),
            iso2: getCountryISO(profile.country_code),
          });
        }
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCountryFlag = (code) => {
    const flags = { '+91': '🇮🇳', '+1': '🇺🇸', '+44': '🇬🇧' };
    return flags[code] || '🌍';
  };

  const getCountryName = (code) => {
    const names = { '+91': 'India', '+1': 'USA', '+44': 'UK' };
    return names[code] || 'Country';
  };

  const getCountryISO = (code) => {
    const isos = { '+91': 'IN', '+1': 'US', '+44': 'GB' };
    return isos[code] || 'XX';
  };

  // Email OTP Handlers
  const handleSendEmailOtp = async () => {
    if (!newEmail.trim() || !/\S+@\S+\.\S+/.test(newEmail)) {
      alert('Please enter a valid email address');
      return;
    }

    try {
      setSendingEmailOtp(true);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        alert('Please log in again');
        return;
      }

      const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/sendEmailOTP`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ email: newEmail.trim() }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setShowEmailOtp(true);
        setShowEmailInput(false);
        setTimeout(() => emailOtpInputs.current[0]?.focus(), 100);
        alert('Verification code sent! Please check your email.');
      } else {
        alert(result.error || 'Failed to send verification code');
      }
    } catch (error) {
      console.error('Error sending email OTP:', error);
      alert('Failed to send verification code. Please try again.');
    } finally {
      setSendingEmailOtp(false);
    }
  };

  const handleEmailOtpChange = (value, index) => {
    const newOtp = [...emailOtp];
    newOtp[index] = value;
    setEmailOtp(newOtp);

    if (value && index < 5) {
      emailOtpInputs.current[index + 1]?.focus();
    }
  };

  const handleVerifyEmailOtp = async () => {
    const otpValue = emailOtp.join('');
    if (otpValue.length !== 6) {
      alert('Please enter the 6-digit verification code');
      return;
    }

    try {
      setVerifyingEmailOtp(true);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        alert('Please log in again');
        return;
      }

      const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/verifyEmailOTP`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          email: newEmail.trim(),
          otp: otpValue,
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setEmail(newEmail);
        setEmailVerified(true);
        alert('Email verified successfully!');
        setShowEmailOtp(false);
        setNewEmail('');
        setEmailOtp(['', '', '', '', '', '']);
      } else {
        alert(result.error || 'Invalid verification code');
      }
    } catch (error) {
      console.error('Error verifying email OTP:', error);
      alert('Failed to verify code. Please try again.');
    } finally {
      setVerifyingEmailOtp(false);
    }
  };

  // Phone OTP Handlers
  const handlePhoneOtpChange = (value, index) => {
    const newOtp = [...phoneOtp];
    newOtp[index] = value;
    setPhoneOtp(newOtp);

    if (value && index < 5) {
      phoneOtpInputs.current[index + 1]?.focus();
    }
  };

  const handleSendPhoneOtp = async () => {
    if (newPhoneNumber.length < 6) {
      alert('Please enter a valid phone number');
      return;
    }

    try {
      setUpdatingPhone(true);
      
      const formattedNumber = selectedCountry.code + newPhoneNumber;
      const apiNumber = formattedNumber.replace('+', '');

      // auth-codes: rate-limited, and refuses a number already verified on
      // another account.
      await sendPhoneCode(apiNumber);
      setShowPhoneOtp(true);
      setTimeout(() => phoneOtpInputs.current[0]?.focus(), 100);
    } catch (error) {
      console.error('Error sending OTP:', error);
      alert(error.message || 'Failed to send verification code');
    } finally {
      setUpdatingPhone(false);
    }
  };

  const handleVerifyPhoneOtp = async () => {
    const otpValue = phoneOtp.join('');
    if (otpValue.length !== 6) {
      alert('Please enter the 6-digit code');
      return;
    }

    try {
      setUpdatingPhone(true);
      
      const formattedNumber = selectedCountry.code + newPhoneNumber;
      const apiNumber = formattedNumber.replace('+', '');

      // The server checks the code AND records the number as verified; the
      // page no longer writes phone_number itself. A database trigger rejects
      // phone_verified from anywhere but that function.
      const result = await verifyPhoneCode(apiNumber, otpValue);
      const verifiedNumber = result?.phone_number ?? formattedNumber;
      await updateUserProfile({ country_code: selectedCountry.code });

      alert('Your phone number is verified');
      setPhoneNumber(verifiedNumber);
      setShowPhoneInput(false);
      setShowPhoneOtp(false);
      setNewPhoneNumber('');
      setPhoneOtp(['', '', '', '', '', '']);
    } catch (error) {
      console.error('Error verifying OTP:', error);
      alert(error.message || 'Failed to verify code');
    } finally {
      setUpdatingPhone(false);
    }
  };

  const handleResetPassword = async () => {
    try {
      const method = emailVerified && email ? 'email' : 'phone';
      
      setResetPasswordState('sending');
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        alert('Please log in again');
        setResetPasswordState('idle');
        return;
      }
      
      let response, result;
      
      if (method === 'email') {
        response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/sendPasswordResetOTP`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ email }),
        });
        result = await response.json();
      } else {
        response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/sendPasswordResetOTPPhone`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ phoneNumber }),
        });
        result = await response.json();
      }
      
      if (response.ok && result.success) {
        setResetPasswordState('sent');
        setResetPasswordTimer(30);
        
        alert(`A password reset code has been sent to your ${method === 'email' ? 'email' : 'phone number'}.`);
        
        // Navigate to reset password page
        navigate('/reset-password', { state: { method, value: method === 'email' ? email : phoneNumber } });
      } else {
        setResetPasswordState('idle');
        alert(result.error || 'Failed to send password reset code');
      }
    } catch (error) {
      console.error('Error sending reset code:', error);
      setResetPasswordState('idle');
      alert('Failed to send password reset code');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="account-details-page">
        <div className="account-details-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="page-title">Account Details</h1>
          </motion.div>

          {/* Email Section */}
          <motion.div 
            className="details-section glass-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h3 className="section-title">Email Address</h3>
            
            {!showEmailInput && !showEmailOtp ? (
              <div className="info-row">
                <div className="info-content">
                  <div className="info-icon">
                    <FiMail />
                  </div>
                  <div className="info-text">
                    <label>Current Email</label>
                    <p>{email || 'No email added'}</p>
                    {emailVerified && email && (
                      <span className="verified-badge">
                        <FiCheck /> Verified
                      </span>
                    )}
                    {!emailVerified && email && (
                      <span className="unverified-badge">
                        <FiAlertCircle /> Not Verified
                      </span>
                    )}
                  </div>
                </div>
                <button
                  className="btn btn-outline"
                  onClick={() => setShowEmailInput(true)}
                >
                  {email ? 'Change' : 'Add'}
                </button>
              </div>
            ) : showEmailInput ? (
              <div className="input-section">
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter email address"
                  className="form-input"
                />
                <div className="button-row">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowEmailInput(false);
                      setNewEmail('');
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleSendEmailOtp}
                    disabled={sendingEmailOtp}
                  >
                    {sendingEmailOtp ? <div className="spinner-small" /> : 'Send Code'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="input-section">
                <p className="otp-label">Enter 6-digit code sent to {newEmail}</p>
                <div className="otp-container">
                  {emailOtp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (emailOtpInputs.current[index] = el)}
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleEmailOtpChange(e.target.value, index)}
                      className="otp-input"
                    />
                  ))}
                </div>
                <div className="button-row">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowEmailOtp(false);
                      setShowEmailInput(true);
                      setEmailOtp(['', '', '', '', '', '']);
                    }}
                  >
                    Back
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleVerifyEmailOtp}
                    disabled={verifyingEmailOtp}
                  >
                    {verifyingEmailOtp ? <div className="spinner-small" /> : 'Verify'}
                  </button>
                </div>
                <button
                  className="resend-button"
                  onClick={handleSendEmailOtp}
                  disabled={sendingEmailOtp}
                >
                  {sendingEmailOtp ? 'Sending...' : 'Resend Code'}
                </button>
              </div>
            )}
          </motion.div>

          {/* Phone Section */}
          <motion.div 
            className="details-section glass-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="section-title">Phone Number</h3>
            
            {!showPhoneInput && !showPhoneOtp ? (
              <div className="info-row">
                <div className="info-content">
                  <div className="info-icon">
                    <FiPhone />
                  </div>
                  <div className="info-text">
                    <label>Current Phone</label>
                    <p>{phoneNumber}</p>
                  </div>
                </div>
                <button
                  className="btn btn-outline"
                  onClick={() => setShowPhoneInput(true)}
                >
                  Change
                </button>
              </div>
            ) : showPhoneInput && !showPhoneOtp ? (
              <div className="input-section">
                <div className="phone-input-container">
                  <button
                    className="country-selector"
                    onClick={() => setShowCountryPicker(true)}
                  >
                    <span>{selectedCountry.flag}</span>
                    <span>{selectedCountry.code}</span>
                    <FiChevronDown />
                  </button>
                  <input
                    type="tel"
                    value={newPhoneNumber}
                    onChange={(e) => setNewPhoneNumber(e.target.value.replace(/\D/g, ''))}
                    placeholder="Enter phone number"
                    maxLength={10}
                    className="form-input"
                  />
                </div>
                <div className="button-row">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowPhoneInput(false);
                      setNewPhoneNumber('');
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleSendPhoneOtp}
                    disabled={updatingPhone}
                  >
                    {updatingPhone ? <div className="spinner-small" /> : 'Send OTP'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="input-section">
                <p className="otp-label">
                  Enter 6-digit code sent to {selectedCountry.code} {newPhoneNumber}
                </p>
                <div className="otp-container">
                  {phoneOtp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (phoneOtpInputs.current[index] = el)}
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handlePhoneOtpChange(e.target.value, index)}
                      className="otp-input"
                    />
                  ))}
                </div>
                <div className="button-row">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowPhoneOtp(false);
                      setShowPhoneInput(false);
                      setNewPhoneNumber('');
                      setPhoneOtp(['', '', '', '', '', '']);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleVerifyPhoneOtp}
                    disabled={updatingPhone}
                  >
                    {updatingPhone ? <div className="spinner-small" /> : 'Verify'}
                  </button>
                </div>
              </div>
            )}
          </motion.div>

          {/* Password Section */}
          <motion.div 
            className="details-section glass-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h3 className="section-title">Password</h3>
            
            <button
              className={`reset-password-button ${
                resetPasswordState === 'sent' ? 'sent' : ''
              }`}
              onClick={handleResetPassword}
              disabled={resetPasswordState === 'sending' || resetPasswordTimer > 0}
            >
              <FiLock />
              <div className="reset-content">
                <span>
                  {resetPasswordState === 'sending'
                    ? 'Sending...'
                    : resetPasswordState === 'sent'
                    ? 'Sent!'
                    : 'Reset Password'}
                </span>
                {resetPasswordTimer > 0 && (
                  <small>Resend in {resetPasswordTimer}s</small>
                )}
              </div>
              {resetPasswordState === 'sending' && <div className="spinner-small" />}
            </button>
            
            <p className="helper-text">
              {emailVerified && email
                ? "We'll send a reset code to your email"
                : "We'll send a reset code to your phone number"}
            </p>
          </motion.div>
        </div>
      </div>

      {showCountryPicker && (
        <PhoneCodePicker
          currentValue={selectedCountry.code}
          onSelect={(country) => {
            setSelectedCountry(country);
            setShowCountryPicker(false);
          }}
          onClose={() => setShowCountryPicker(false)}
        />
      )}
    </DashboardLayout>
  );
};

export default AccountDetails;